import { useDismiss } from "@/common/hooks/useDismiss";
import { ApiError } from "@/common/lib/api";
import type { AccountRole } from "@/features/accounts/api/accountsApi";
import { useCreateInvitation } from "@/features/accounts/api/invitationsApi";
import { ChevronDown, X } from "lucide-react";
import { type FormEvent, useRef, useState } from "react";
import { toast } from "sonner";

const ROLE_OPTIONS: { value: AccountRole; label: string }[] = [
  { value: "admin", label: "Admin" },
  { value: "operator", label: "Operador" },
  { value: "viewer", label: "Visor" },
];

type Props = {
  accountId: string;
  open: boolean;
  onClose: () => void;
};

export function InviteMemberDialog({ accountId, open, onClose }: Props) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AccountRole>("operator");
  const [roleOpen, setRoleOpen] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const roleSelectorRef = useRef<HTMLDivElement>(null);
  const mutation = useCreateInvitation(accountId);

  useDismiss({
    enabled: roleOpen,
    containerRef: roleSelectorRef,
    onDismiss: () => setRoleOpen(false),
  });

  if (!open) return null;

  function handleClose() {
    setEmail("");
    setRole("operator");
    setEmailError(null);
    onClose();
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      setEmailError("Ingresá un email válido.");
      return;
    }
    setEmailError(null);

    try {
      await mutation.mutateAsync({ email: trimmed, role });
      toast.success(`Invitación enviada a ${trimmed}.`);
      handleClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        toast.error("Demasiados intentos. Esperá un momento.");
      } else {
        toast.error("No se pudo enviar la invitación. Intentá de nuevo.");
      }
    }
  }

  const selectedRoleLabel = ROLE_OPTIONS.find((o) => o.value === role)?.label ?? "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
        onKeyDown={(e) => {
          if (e.key === "Escape") handleClose();
        }}
      />
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-app-border bg-app-surface p-6 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-semibold text-app-ink-1 text-lg tracking-tight">Invitar miembro</h2>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-1 text-app-ink-3 hover:bg-app-surface-2"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="invite-email" className="font-medium text-app-ink-2 text-sm">
              Email
            </label>
            <input
              id="invite-email"
              type="email"
              inputMode="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(null);
              }}
              placeholder="invitado@ejemplo.com"
              aria-invalid={Boolean(emailError)}
              className="h-11 w-full rounded-xl border border-app-border bg-app-bg px-4 text-app-ink-1 text-base placeholder:text-app-ink-3 focus:border-primary-500/60 focus:outline-none focus:ring-1 focus:ring-primary-500/40"
              disabled={mutation.isPending}
            />
            {emailError && (
              <p role="alert" className="text-rose-500 text-xs">
                {emailError}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="invite-role" className="font-medium text-app-ink-2 text-sm">
              Rol
            </label>
            <div ref={roleSelectorRef} className="relative">
              <button
                id="invite-role"
                type="button"
                onClick={() => setRoleOpen((o) => !o)}
                disabled={mutation.isPending}
                className="flex h-11 w-full items-center justify-between rounded-xl border border-app-border bg-app-bg px-4 text-app-ink-1 text-base focus:outline-none"
                aria-haspopup="listbox"
                aria-expanded={roleOpen}
              >
                <span>{selectedRoleLabel}</span>
                <ChevronDown className="h-4 w-4 text-app-ink-3" />
              </button>
              {roleOpen && (
                <ul className="absolute top-full right-0 left-0 z-20 mt-1 overflow-hidden rounded-xl border border-app-border bg-app-surface shadow">
                  {ROLE_OPTIONS.map((opt) => (
                    <li
                      key={opt.value}
                      aria-selected={opt.value === role}
                      onClick={() => {
                        setRole(opt.value);
                        setRoleOpen(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setRole(opt.value);
                          setRoleOpen(false);
                        }
                      }}
                      className={`cursor-pointer px-4 py-2.5 text-sm transition-colors hover:bg-app-surface-2 ${
                        opt.value === role ? "font-medium text-primary-600" : "text-app-ink-1"
                      }`}
                    >
                      {opt.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="mt-2 flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex h-11 flex-1 items-center justify-center rounded-xl border border-app-border font-medium text-app-ink-2 text-sm transition-colors hover:bg-app-surface-2"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex h-11 flex-1 items-center justify-center rounded-xl bg-app-ink-1 font-medium text-sm text-white transition-colors hover:bg-[#1e211d] disabled:opacity-60"
            >
              {mutation.isPending ? "Enviando…" : "Invitar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
